import { useFormik } from "formik";
import {
  UpdateAgreementRequestDTO,
  usePermission,
  useToast,
  useUpdateAgreementMutation,
} from "@/hooks";
import { useTranslation } from "react-i18next";
import { Label, Input, Button } from "@/components/ui";
import { ConvenantValidationSchema } from "@/shared";
import { Agreement } from "@/types";
import { DisplayError } from "@/utils";
import { AlertDialogDelete } from "../../dialogs";

type FormAgreementProps = {
  handleClose: () => void;
  agreement: Agreement;
};

export function FormUpdateAgreement({
  handleClose,
  agreement,
}: FormAgreementProps) {
  const { t } = useTranslation();
  const { checkPermission } = usePermission();
  const { toast } = useToast();

  const initialValues = {
    id: agreement.id || "",
    name: agreement.name || "",
    phone: agreement.phone || "",
    commission: agreement.commission || "0",
  };

  const { mutateAsync, isLoading } = useUpdateAgreementMutation();
  const { handleSubmit, getFieldProps, errors, touched, resetForm } = useFormik(
    {
      initialValues,
      validationSchema: ConvenantValidationSchema(),
      onSubmit: async (values) => {
        handleUpdateConvenantSubmit(values);
      },
    }
  );

  async function handleUpdateConvenantSubmit(
    request: UpdateAgreementRequestDTO
  ) {
    try {
      await mutateAsync(request, {
        onSuccess: () => {
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.updated successfully"),
          });
          resetForm();
          handleClose();
        },
        onError: (error: any) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message")),
            variant: "destructive",
          });
        },
      });
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-10 px-4 pb-8">
      <div className="space-y-2">
        <div className="flex flex-col gap-2">
          <Label>{t("LABEL.name")}</Label>
          <Input
            isInvalid={touched.name && errors.name}
            isValid={touched.name && !errors.name}
            {...getFieldProps("name")}
            messageError={errors.name}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label>{t("LABEL.phone")}</Label>
          <Input
            inputPhone
            isInvalid={touched.phone && errors.phone}
            isValid={touched.phone && !errors.phone}
            {...getFieldProps("phone")}
            messageError={errors.phone}
          />
        </div>
        {/*
        <div className="flex flex-col gap-2">
          <Label>{t("LABEL.comission")} (%)</Label>
          <Input
            type="number"
            isInvalid={touched.commission && errors.commission}
            isValid={touched.commission && !errors.commission}
            {...getFieldProps("commission")}
            messageError={errors.commission}
          />
        </div>
        */}
      </div>
      <div className="w-full space-y-2">
        <Button isLoading={isLoading} className="w-full" disabled={!checkPermission("convenants", "edit convenants")}>
          {t("LABEL.save")}
        </Button>
        <AlertDialogDelete
          endpoint={`/covenants/${agreement.id}`}
          queryKey="covenants"
          callBack={handleClose}
        >
          <Button
            type="button"
            className="w-full bg-destructive hover:bg-destructive/80"
            disabled={!checkPermission("convenants", "delete convenants")}
          >
            {t("LABEL.delete")}
          </Button>
        </AlertDialogDelete>
      </div>
    </form>
  );
}
